/**
 * useRecompute — POST /v1/clinics/{clinic_id}/recompute
 *
 * Re-runs the scoring engine synchronously (handoff §2: blocks for ~3-5s).
 * No polling, no background-job UX. Show a spinner; on completion the
 * dashboard query is invalidated and the new snapshot is fetched.
 *
 * Also invalidates the scoring-runs admin list since a new run row is
 * created.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getApiClient } from "@/api/client";
import type { RecomputeResponse } from "@/api/models";
import { queryKeys } from "./queryKeys";

export interface RecomputeVariables {
  year: number;
}

export function useRecompute(clinicId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ year }: RecomputeVariables): Promise<RecomputeResponse> => {
      const { data } = await getApiClient().post<RecomputeResponse>(
        `/v1/clinics/${clinicId}/recompute`,
        null,
        { params: { performance_year: year }, timeout: 60_000 },
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.byClinic(clinicId) });
      void qc.invalidateQueries({ queryKey: queryKeys.scoringRuns.all });
    },
  });
}
